'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { Kind, } from '@coveo/headless/ssr-commerce';
import { useEffect, useState } from 'react';
function getController(controllers, key) {
    return controllers[key];
}
/**
 * Providers take care of displaying your page with the static
 * state, and then hydrating the state and displaying the page
 * with the hydrated state. They are required for your controller hooks to function.
 *
 * See [Create providers](docs.coveo.com/en/obif0156/#create-providers).
 *
 * @group Providers
 */
export function buildProviderWithDefinition(definition) {
    return function WrappedProvider({ staticState, navigatorContext, children, }) {
        const [hydratedState, setHydratedState] = useState(undefined);
        definition.setNavigatorContextProvider(() => navigatorContext);
        useEffect(() => {
            const { searchActions, controllers } = staticState;
            const hydrateArguments = {};
            for (const [key, controller] of Object.entries(controllers)) {
                const typedController = controller;
                switch (typedController._kind) {
                    case Kind.Cart: {
                        const cart = getController(controllers, key);
                        hydrateArguments[key] = {
                            initialState: {
                                items: cart.state.items,
                            },
                        };
                        break;
                    }
                    case Kind.Context: {
                        const context = getController(controllers, key);
                        hydrateArguments[key] = context.state;
                        break;
                    }
                    case Kind.ParameterManager: {
                        const parameterManager = getController(controllers, key);
                        hydrateArguments[key] = {
                            initialState: {
                                parameters: parameterManager.state.parameters,
                            },
                        };
                        break;
                    }
                    case Kind.Recommendations: {
                        const recommendations = getController(controllers, key);
                        hydrateArguments[key] = {
                            productId: recommendations.state.productId,
                        };
                        break;
                    }
                }
            }
            const args = {
                searchActions,
            };
            if (hydrateArguments) {
                args.controllers = hydrateArguments;
            }
            // @ts-expect-error Casting to loose definition since we don't need the inferred controllers here
            const looseDefinition = definition;
            looseDefinition.hydrateStaticState(args).then(({ engine, controllers }) => {
                setHydratedState({
                    engine,
                    controllers: controllers,
                });
            });
        }, [staticState]);
        if (hydratedState) {
            return (_jsx(definition.HydratedStateProvider, { engine: hydratedState.engine, controllers: hydratedState.controllers, children: children }));
        }
        const StaticStateProviderWithAnyControllers = definition.StaticStateProvider;
        return (_jsx(StaticStateProviderWithAnyControllers, { controllers: staticState.controllers, children: children }));
    };
}
//# sourceMappingURL=providers.js.map